/* eslint-disable no-console */
import React, { useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { Form, Message } from "semantic-ui-react";
import {
  initialValues,
  validationSchema,
  isWeekday,
  clampDateToCycle,
  attendanceOptions,
} from "./AttendanceForm.form";
import { Attendance } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { withEmptyOption } from "../../../../utils";

const attendanceController = new Attendance();

export function AttendanceForm(props) {
  const {
    onClose,
    onReload,
    student,
    gradeId,
    academicCycleId,
    academicCycle,
  } = props;
  const { accessToken, user } = useAuth();

  const cycleStartMoment = academicCycle?.startDate
    ? moment.utc(academicCycle.startDate)
    : null;
  const cycleEndMoment = academicCycle?.endDate
    ? moment.utc(academicCycle.endDate)
    : null;

  const cycleStartDate = cycleStartMoment
    ? cycleStartMoment.format("YYYY-MM-DD")
    : undefined;
  const cycleEndDate = cycleEndMoment
    ? cycleEndMoment.format("YYYY-MM-DD")
    : undefined;

  const [submissionError, setSubmissionError] = useState("");

  const formik = useFormik({
    initialValues: initialValues(academicCycle),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmissionError("");

      try {
        const attendanceData = {
          attendanceDate: values.attendanceDate,
          academicCycleId: parseInt(academicCycleId),
          recordedBy: user.id,
          attendances: [
            {
              studentId: student.id,
              status: values.status,
              notes: values.notes,
              ...(values.status !== "ausente" && values.status !== "justificado" && {
                arrivalTime: values.arrivalTime,
              }),
            },
          ],
        };

        await attendanceController.createMassiveAttendance(
          accessToken,
          gradeId,
          attendanceData
        );

        onReload();
        onClose();
      } catch (error) {
        setSubmissionError(
          error?.message || "Error al registrar la asistencia"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleStatusChange = (status) => {
    formik.setFieldValue("status", status);

    // Ajustar hora de llegada según el estado
    if (status === "tardanza") {
      formik.setFieldValue("arrivalTime", "07:45:00");
    } else if (status === "presente") {
      formik.setFieldValue("arrivalTime", "07:30:00");
    } else if (status === "ausente" || status === "justificado") {
      formik.setFieldValue("arrivalTime", "");
    }
  };

  const handleDateChange = (value) => {
    const parsedDate = moment.utc(value, "YYYY-MM-DD", true);

    if (!parsedDate.isValid()) {
      formik.setFieldError(
        "attendanceDate",
        "Por favor, seleccione una fecha válida"
      );
      return;
    }

    // Validar que sea día de semana (lunes a viernes)
    if (!isWeekday(parsedDate)) {
      formik.setFieldError(
        "attendanceDate",
        "Solo se puede registrar asistencia de lunes a viernes"
      );
      formik.setFieldValue("attendanceDate", value);
      return;
    }

    const normalizedDate = clampDateToCycle(
      parsedDate,
      cycleStartMoment,
      cycleEndMoment
    ).format("YYYY-MM-DD");
    formik.setFieldValue("attendanceDate", normalizedDate);
    formik.setFieldError("attendanceDate", undefined);
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      {submissionError && <Message negative content={submissionError} />}

      <Form.Field>
        <Form.Input
          label="Estudiante"
          value={`${student.User.firstname} ${student.User.lastname}`}
          readOnly
        />
      </Form.Field>

      <Form.Field>
        <Form.Input
          label="Fecha (Solo días hábiles: Lunes a Viernes)"
          type="date"
          name="attendanceDate"
          value={formik.values.attendanceDate}
          onChange={(e) => handleDateChange(e.target.value)}
          required
          min={cycleStartDate}
          max={cycleEndDate}
          error={formik.errors.attendanceDate}
        />
      </Form.Field>

      <Form.Select
        className="edunova-select"
        label="Estado de Asistencia"
        placeholder="Seleccionar estado"
        fluid
        clearable
        options={withEmptyOption(attendanceOptions, "Seleccionar estado")}
        name="status"
        value={formik.values.status}
        onChange={(e, { value }) => handleStatusChange(value)}
        error={formik.errors.status}
      />

      <Form.Field>
        <Form.Input
          label="Hora de Llegada (solo para Presente o Tardanza)"
          type="time"
          name="arrivalTime"
          value={formik.values.arrivalTime}
          onChange={(e) => formik.setFieldValue("arrivalTime", e.target.value)}
          disabled={formik.values.status === "ausente" || formik.values.status === "justificado"}
          error={formik.errors.arrivalTime}
        />
      </Form.Field>

      <Form.Field>
        <Form.TextArea
          label="Notas (opcional)"
          name="notes"
          value={formik.values.notes}
          onChange={(e) => formik.setFieldValue("notes", e.target.value)}
          placeholder="Agregar notas..."
          error={formik.errors.notes}
        />
      </Form.Field>

      <Form.Button type="submit" fluid primary loading={formik.isSubmitting}>
        Registrar Asistencia
      </Form.Button>
    </Form>
  );
}
